import React from "react";
import { useTranslation } from "react-i18next";
import "./LanguageSelector.scss";
import i18n from "i18next";
import { useRef } from "react";
import { readLanguageFromCookie } from "../GeneralUtils";

export type LanguageSelectorProps = {
    name?: string;
    className?: string;
    disabled?: boolean;
    placeholder?: string;
    clearErrors?: any;
    children?: any;
    onClick?: (language: string) => void;
};

const LanguageSelector = React.forwardRef((props: LanguageSelectorProps) => {
    const languageDropdownRef = useRef(null);

    const { i18n } = useTranslation();
    const [open, setOpen] = React.useState(false);
    const [selection, setSelection] = React.useState(-1);
    const selectedLabel = selection < 0 ? props.children[0].props.label : props.children[selection].props.label;
    const selectedValue = selection < 0 ? props.children[0].props.value : props.children[selection].props.value;

    React.useEffect(() => {
        if (open) {
            document.addEventListener("click", closeOpenLanguageDropdown);
        } else {
            document.removeEventListener("click", closeOpenLanguageDropdown);
        }

        const language = readLanguageFromCookie();

        let index = 0;
        props.children.forEach((option: any) => {
            if (option.props.value === language) {
                setSelection(index);
            }
            index++;
        });

        return () => {
            document.removeEventListener("click", closeOpenLanguageDropdown);
        };
    }, [open]);

    const handleClick = (event: React.SyntheticEvent<HTMLElement> | Event) => {
        if (props.disabled) {
            return;
        }

        event.stopPropagation();
        setOpen(!open);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.code === "Enter" || e.code === "NumpadEnter") {
            setOpen(!open);
        }
    };

    const closeOpenLanguageDropdown = (e: any) => {
        // @ts-ignore
        if (languageDropdownRef.current && !languageDropdownRef.current.contains(e.target)) {
            setOpen(false);
        }
    };

    const handleListClick = (event: React.MouseEvent<HTMLUListElement> | React.KeyboardEvent<HTMLUListElement>) => {
        console.log("Is keyboard event: " + event.type);
        if (event.nativeEvent instanceof KeyboardEvent) {
            if (event.nativeEvent.code !== "Enter" && event.nativeEvent.code !== "NumpadEnter") {
                return;
            }
        }

        const selectedIndex = (event.target as HTMLElement).getAttribute("data-index");
        if (selectedIndex) {
            const newSelectedIndex = parseInt(selectedIndex);
            if (props.children[newSelectedIndex] !== undefined && props.children[newSelectedIndex].props.value !== null) {
                setSelection(newSelectedIndex);
                setOpen(false);
                const language = props.children[newSelectedIndex].props.value;
                changeLanguage(language);

                if (props.onClick) {
                    props.onClick(language);
                }
            } else {
                event.preventDefault();
            }
        } else {
            console.error("Attribute data-index not found on element:", event.target);
        }
    };

    const children = React.Children.map(props.children, (child, index) => {
        return React.cloneElement(child, {
            dataIndex: index,
        });
    });

    return (
        <div ref={languageDropdownRef} className={"language-wrapper " + props.className}>
            <div className="select-language" onClick={handleClick} onKeyDown={handleKeyDown} tabIndex={0}>
                <i className={"link-icon icon-global-service"} />
                <span>{selectedLabel}</span>
                <i className={open ? "icon-arrow-up" : "icon-arrow-down"} />
            </div>

            {open && (
                <div className="language-datalayer-wrapper">
                    <ul className="language-datalayer" onClick={handleListClick} onKeyDown={handleListClick} tabIndex={0}>
                        {children}
                    </ul>
                </div>
            )}
        </div>
    );
});

export const changeLanguage = (language: string) => {
    i18n.changeLanguage(language);
    document.cookie = "Language=" + language + "; path=/";
    localStorage.setItem("Language", language);

    const languageEvent = new CustomEvent("languagechange", {
        detail: {
            language: language,
        },
        bubbles: true,
        cancelable: true,
        composed: false,
    });

    window.dispatchEvent(languageEvent);
};

export type LanguageItemProps = {
    value: string;
    show: boolean;
    label: string;
    dataIndex?: number;
};

export const LanguageItem = function (props: LanguageItemProps) {
    if (props.show) {
        return (
            <li data-index={props.dataIndex} tabIndex={0}>
                <span data-index={props.dataIndex} style={{ display: "inline-block" }}>
                    {props.label}
                </span>
            </li>
        );
    } else {
        return <></>;
    }
};

export default LanguageSelector;
